/**
 * Frontend mirror of the backend `ANSWER_TIERS` catalog in
 * `apps/edge-llm-api/edge_llm_api/model_catalog.py`.
 *
 * Keep in sync with the backend — this drives the UI (labels, order, gating)
 * and the tier value threaded into analyst requests.
 *
 * Deliberately carries no billing numbers: an answer is billed at its real
 * cost on the server (edge-companion-api metering.ts), where the tier only
 * sets the ceiling reserved. The UI needs id/label/gating only.
 */

import { tierMeetsRequirement } from '@/context/plan-constraint-utils'
import type { PlanTier } from '@/context/plan-constraint-utils'

// ─── Tier identifiers ────────────────────────────────────────────────────────

export type AnswerTierId = 'fast' | 'normal' | 'heavy'

// ─── Tier metadata ────────────────────────────────────────────────────────────

export interface AnswerTierMeta {
  id: AnswerTierId
  label: string
  /** Lowest plan that unlocks this tier. */
  minPlan: PlanTier
}

export const ANSWER_TIER_META: AnswerTierMeta[] = [
  { id: 'fast', label: 'Fast', minPlan: 'basic' },
  { id: 'normal', label: 'Normal', minPlan: 'pro' },
  { id: 'heavy', label: 'Heavy', minPlan: 'pro_plus' },
]

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Tiers the given plan is allowed to use, in ascending order (fast first).
 */
export function allowedTiers(plan: PlanTier): AnswerTierId[] {
  return ANSWER_TIER_META.filter((t) => tierMeetsRequirement(plan, t.minPlan)).map((t) => t.id)
}

/**
 * Default tier for a plan:
 * - `normal` when the plan allows it (pro+),
 * - otherwise the highest tier the plan allows (fast for basic).
 */
export function defaultTierForPlan(plan: PlanTier): AnswerTierId {
  const tiers = allowedTiers(plan)
  // prefer 'normal' if available; otherwise take the last (highest) allowed
  if (tiers.includes('normal')) return 'normal'
  return tiers[tiers.length - 1] ?? 'fast'
}

/**
 * Clamp a stored tier to the plan's allowed set. If the stored tier is not
 * allowed, return the default for the plan.
 */
export function clampTierToPlan(tier: AnswerTierId, plan: PlanTier): AnswerTierId {
  const tiers = allowedTiers(plan)
  return tiers.includes(tier) ? tier : defaultTierForPlan(plan)
}
